/**
 * Types for structured data responses in chat messages.
 *
 * This enables the backend to return structured data that can be
 * rendered as interactive elements (modals, buttons, etc) in the chat.
 *
 * Convention: When a message contains structured data, it uses the format:
 * <!-- STRUCTURED_DATA:{ ... JSON ... } -->
 * Followed by the regular text content.
 */

/**
 * A single field in the data modal
 */
export interface StructuredDataField {
  /** Label displayed to the user */
  label: string
  /** Field value (null if not available) */
  value: string | null
  /** Whether this field is required/critical */
  required?: boolean
  /** Optional field name for identification */
  name?: string
}

/**
 * Generic structured data that can be displayed in a modal
 */
export interface StructuredData {
  /** Unique type identifier for the structured data */
  type: 'helpdesk' | 'service_order' | 'warranty' | 'generic'
  /** Modal title */
  title: string
  /** Subtitle or description */
  subtitle?: string
  /** @deprecated Prefer `iconName` (Lucide). Kept for legacy emoji strings. */
  icon?: string
  /** Preferred: Lucide icon name (e.g. `"file-text"`, `"ticket"`) from the design-system Icon atom. */
  iconName?: string
  /** Array of fields to display */
  fields: StructuredDataField[]
  /** Optional action button configuration */
  action?: {
    label: string
    url?: string
    type?: 'copy_all' | 'open_url' | 'custom'
  }
  /** Trigger button text shown in the chat */
  triggerLabel?: string
  /** @deprecated Prefer `triggerIconName`. Kept for legacy emoji strings. */
  triggerIcon?: string
  /** Preferred: Lucide icon name for the trigger button. */
  triggerIconName?: string
}

/**
 * Helpdesk-specific structured data
 */
export interface HelpdeskData extends StructuredData {
  type: 'helpdesk'
  /** Service order number */
  serviceOrder?: string
  /** Customer information */
  customer?: string
  /** Vehicle chassis */
  chassis?: string
}

/**
 * Regular expression to detect structured data in message content
 */
export const STRUCTURED_DATA_REGEX = /<!--\s*STRUCTURED_DATA:([\s\S]*?)-->/

/**
 * Parse structured data from message content
 *
 * @param content - The message content that may contain structured data
 * @returns Object with parsed data and remaining text content, or null if no structured data found
 */
export function parseStructuredData(content: string): {
  data: StructuredData
  textContent: string
} | null {
  const match = content.match(STRUCTURED_DATA_REGEX)

  if (!match) {
    return null
  }

  try {
    const jsonStr = match[1].trim()
    const data = JSON.parse(jsonStr) as StructuredData

    // Validate required fields
    if (!data.type || !data.title || !Array.isArray(data.fields)) {
      // eslint-disable-next-line no-console
      console.warn('Invalid structured data format:', data)
      return null
    }

    // Extract remaining text content (after the structured data tag)
    const textContent = content.replace(STRUCTURED_DATA_REGEX, '').trim()

    return { data, textContent }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse structured data:', error)
    return null
  }
}

/**
 * Check if a message contains structured data
 */
export function hasStructuredData(content: string): boolean {
  return STRUCTURED_DATA_REGEX.test(content)
}

/**
 * Calculate completeness percentage based on filled fields
 *
 * - total: all fields in the modal
 * - filled: fields with value (not null/empty)
 * - requiredMissing: required fields without value
 * - percentage: based on all fields, not just required
 */
export function calculateCompleteness(fields: StructuredDataField[]): {
  percentage: number
  filled: number
  total: number
  requiredMissing: number
} {
  const total = fields.length
  const filledFields = fields.filter(
    f => f.value !== null && f.value !== '' && f.value !== undefined
  )
  const requiredMissing = fields.filter(
    f =>
      f.required === true &&
      (f.value === null || f.value === '' || f.value === undefined)
  ).length

  return {
    percentage:
      total > 0 ? Math.round((filledFields.length / total) * 100) : 100,
    filled: filledFields.length,
    total,
    requiredMissing,
  }
}
